import { AmendmentChange, ClinicalNote } from '../types'

export type AmendableNote = Omit<ClinicalNote, 'editHistory' | 'addenda'>

export type AmendableField = {
  /** Dot path into the clinical note, e.g. "socialHistory.alcohol". */
  path: string
  label: string
  kind: 'text' | 'longtext' | 'list'
  group: string
}

/** Every clinician-editable section of the consultation note. */
export const amendableFields: AmendableField[] = [
  { path: 'presentingComplaint', label: 'Presenting complaint', kind: 'longtext', group: 'History' },
  { path: 'historyOfPresentingComplaint', label: 'History of presenting complaint', kind: 'longtext', group: 'History' },
  { path: 'pastMedicalHistory', label: 'Past medical history', kind: 'list', group: 'History' },
  { path: 'drugHistory', label: 'Drug history', kind: 'longtext', group: 'History' },
  { path: 'familyMedicalHistory', label: 'Family medical history', kind: 'list', group: 'History' },
  { path: 'familyMedicalHistoryOther', label: 'Family history — other', kind: 'text', group: 'History' },
  { path: 'obgynHistory', label: 'Obstetric & gynaecological history', kind: 'longtext', group: 'History' },
  { path: 'socialHistory.alcohol', label: 'Alcohol', kind: 'text', group: 'Social history' },
  { path: 'socialHistory.smoking', label: 'Smoking', kind: 'text', group: 'Social history' },
  { path: 'socialHistory.occupation', label: 'Occupation', kind: 'text', group: 'Social history' },
  { path: 'socialHistory.mentalStatus', label: 'Mental status', kind: 'text', group: 'Social history' },
  { path: 'reviewOfSystems.rs', label: 'Respiratory (RS)', kind: 'text', group: 'Review of systems' },
  { path: 'reviewOfSystems.cvs', label: 'Cardiovascular (CVS)', kind: 'text', group: 'Review of systems' },
  { path: 'reviewOfSystems.cns', label: 'Central nervous (CNS)', kind: 'text', group: 'Review of systems' },
  { path: 'reviewOfSystems.git', label: 'Gastrointestinal (GIT)', kind: 'text', group: 'Review of systems' },
  { path: 'reviewOfSystems.gut', label: 'Genitourinary (GUT)', kind: 'text', group: 'Review of systems' },
  { path: 'reviewOfSystems.mss', label: 'Musculoskeletal (MSS)', kind: 'text', group: 'Review of systems' },
  { path: 'reviewOfSystems.is', label: 'Integumentary (IS)', kind: 'text', group: 'Review of systems' },
  { path: 'onExamination', label: 'On examination', kind: 'longtext', group: 'Examination' },
  { path: 'onExaminationDetails.gc', label: 'General condition', kind: 'text', group: 'Examination' },
  { path: 'onExaminationDetails.febrile', label: 'Febrile', kind: 'text', group: 'Examination' },
  { path: 'onExaminationDetails.pjc', label: 'PJC', kind: 'text', group: 'Examination' },
  { path: 'onExaminationDetails.s1s2', label: 'S1 S2', kind: 'text', group: 'Examination' },
  { path: 'onExaminationDetails.rs', label: 'RS (examination)', kind: 'text', group: 'Examination' },
  { path: 'onExaminationDetails.pa', label: 'P/A', kind: 'text', group: 'Examination' },
  { path: 'significantFindings', label: 'Significant findings', kind: 'list', group: 'Findings & diagnosis' },
  { path: 'workingDiagnosis', label: 'Working diagnosis', kind: 'list', group: 'Findings & diagnosis' },
]

/** Form value for a field: lists are edited one item per line. */
export function readField(note: AmendableNote, field: AmendableField): string {
  const value = field.path
    .split('.')
    .reduce<unknown>(
      (obj, key) =>
        obj && typeof obj === 'object'
          ? (obj as Record<string, unknown>)[key]
          : undefined,
      note,
    )
  if (field.kind === 'list') return Array.isArray(value) ? value.join('\n') : ''
  return typeof value === 'string' ? value : value == null ? '' : String(value)
}

/** Returns a new note with the form value written back at the field path. */
export function writeField(
  note: AmendableNote,
  field: AmendableField,
  value: string,
): AmendableNote {
  const stored: unknown =
    field.kind === 'list'
      ? value
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean)
      : value
  const [head, child] = field.path.split('.')
  const record = note as unknown as Record<string, unknown>
  if (!child) return { ...record, [head]: stored } as unknown as AmendableNote
  const parent = (record[head] as Record<string, unknown> | undefined) ?? {}
  return {
    ...record,
    [head]: { ...parent, [child]: stored },
  } as unknown as AmendableNote
}

/** Field-level before/after diff between the original and amended note. */
export function diffNotes(
  original: AmendableNote,
  amended: AmendableNote,
): AmendmentChange[] {
  return amendableFields.flatMap((field) => {
    const previous = normalise(readField(original, field), field)
    const next = normalise(readField(amended, field), field)
    return previous === next
      ? []
      : [{ field: field.path, label: field.label, previous, amended: next }]
  })
}

function normalise(value: string, field: AmendableField) {
  if (field.kind === 'list')
    return value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)
      .join('\n')
  return value.trim()
}
